'use client';

import React, { useState, useEffect, useRef } from 'react';

/* ─── Keyframe CSS ───────────────────────────────────────── */
const STYLES = `
  @keyframes floatLaptop {
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-8px); }
  }
  @keyframes floatTablet {
    0%, 100% { transform: translateY(0px) rotate(-1deg); }
    50% { transform: translateY(-6px) rotate(-0.5deg); }
  }
  @keyframes floatMobile {
    0%, 100% { transform: translateY(0px) rotate(1deg); }
    50% { transform: translateY(-5px) rotate(0.5deg); }
  }
  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  @keyframes glowPulse {
    0%, 100% { opacity: 0.3; }
    50% { opacity: 0.6; }
  }
  @keyframes pulseDot {
    0%, 100% { opacity: 0.6; transform: scale(0.9); }
    50% { opacity: 1; transform: scale(1.1); }
  }
  @keyframes countUp {
    from { opacity: 0; transform: translateY(4px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @keyframes slideIn {
    from { opacity: 0; transform: translateX(8px); }
    to { opacity: 1; transform: translateX(0); }
  }
  @keyframes barGrow {
    from { width: 0%; }
  }
  .tablet-slider::-webkit-scrollbar { display: none; }
  .laptop-float { animation: floatLaptop 8s ease-in-out infinite; }
  .tablet-float { animation: floatTablet 10s ease-in-out infinite 1.2s; }
  .mobile-float { animation: floatMobile 7s ease-in-out infinite 0.6s; }
  .signal-slider::-webkit-scrollbar { display: none; }
  .signal-slider { -ms-overflow-style: none; scrollbar-width: none; }
`;

/* ─── Laptop Screen Content ─────────────────────────────── */
const LaptopContent = ({ visible }: { visible: boolean }) => (
  <div style={{ height: '100%', display: 'flex', overflow: 'hidden' }}>
    {/* Sidebar */}
    <div style={{
      width: 80, background: 'rgba(246,245,240,0.98)',
      borderRight: '1px solid #E5EDE9', padding: '10px 0', flexShrink: 0,
    }}>
      <div style={{ padding: '0 10px 8px', borderBottom: '1px solid #E5EDE9', marginBottom: 6 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <div style={{ width: 12, height: 12, borderRadius: '50%', background: '#1F4D3F', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg viewBox="0 0 10 10" fill="none" width="7" height="7">
              <circle cx="5" cy="5" r="2" fill="white" />
            </svg>
          </div>
          <span style={{ fontSize: 7, fontWeight: 800, color: '#1F4D3F', letterSpacing: '0.06em' }}>VINA</span>
        </div>
      </div>
      {['Overview', 'Revenue', 'Inventory', 'Customers', 'Purchasing', 'Cash Flow', 'Reports', 'Alerts'].map((item, i) => (
        <div key={item} style={{
          padding: '4px 10px',
          background: i === 0 ? 'rgba(31,77,63,0.1)' : 'transparent',
          borderLeft: i === 0 ? '2px solid #1F4D3F' : '2px solid transparent',
        }}>
          <span style={{ fontSize: 6.5, color: i === 0 ? '#1F4D3F' : '#9CA3AF', fontWeight: i === 0 ? 700 : 400 }}>{item}</span>
        </div>
      ))}
    </div>

    {/* Main content */}
    <div style={{ flex: 1, padding: '10px 12px', overflow: 'hidden', display: 'flex', flexDirection: 'column', gap: 8 }}>
      {/* Top bar */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontSize: 9, fontWeight: 700, color: '#143A33' }}>Good morning, Storeex 247.</div>
          <div style={{ fontSize: 6.5, color: '#9CA3AF' }}>Here&apos;s what&apos;s happening across your business.</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <div style={{
            padding: '3px 8px', borderRadius: 6,
            background: 'rgba(255,255,255,0.9)', border: '1px solid #DDE7E3',
            display: 'flex', alignItems: 'center', gap: 3,
          }}>
            <svg viewBox="0 0 12 12" fill="none" width="8" height="8">
              <circle cx="5" cy="5" r="3.5" stroke="#9CA3AF" strokeWidth="1" />
              <path d="M8 8l2 2" stroke="#9CA3AF" strokeWidth="1" strokeLinecap="round" />
            </svg>
            <span style={{ fontSize: 6, color: '#9CA3AF' }}>Ask VINA anything...</span>
          </div>
          <div style={{ width: 18, height: 18, borderRadius: '50%', background: '#1F4D3F', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ fontSize: 7, color: 'white', fontWeight: 700 }}>S</span>
          </div>
        </div>
      </div>

      {/* KPI row */}
      <div style={{ display: 'flex', gap: 6 }}>
        {[
          { label: 'Revenue', val: '₹18.4L', delta: '+18%', sub: 'vs last 30 days' },
          { label: 'Orders', val: '642', delta: '+14%', sub: 'vs last 30 days' },
          { label: 'Gross Margin', val: '24.6%', delta: '+3.2%', sub: 'vs last 30 days' },
          { label: 'Outlets', val: '24', delta: '+2', sub: 'vs last 30 days' },
        ].map(({ label, val, delta, sub }, i) => (
          <div key={label} style={{
            flex: 1, background: 'white', borderRadius: 8, padding: '6px 7px',
            border: '1px solid #E5EDE9',
            boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
            animation: visible ? `countUp 0.4s ease-out ${0.1 + i * 0.1}s both` : 'none',
          }}>
            <div style={{ fontSize: 6, color: '#9CA3AF', marginBottom: 2 }}>{label}</div>
            <div style={{ fontSize: 12, fontWeight: 800, color: '#143A33' }}>{val}</div>
            <div style={{ fontSize: 6, color: '#16A34A', fontWeight: 600 }}>{delta}</div>
            <div style={{ fontSize: 5.5, color: '#D1D5DB' }}>{sub}</div>
          </div>
        ))}
      </div>

      {/* Charts row */}
      <div style={{ display: 'flex', gap: 6, flex: 1 }}>
        {/* Revenue Trend */}
        <div style={{ flex: 1, background: 'white', borderRadius: 8, padding: '7px 8px', border: '1px solid #E5EDE9' }}>
          <div style={{ fontSize: 7, fontWeight: 700, color: '#143A33', marginBottom: 2 }}>Revenue Trend</div>
          <div style={{ fontSize: 10, fontWeight: 800, color: '#1F4D3F' }}>₹18.4L</div>
          <div style={{ fontSize: 6, color: '#16A34A', marginBottom: 6 }}>+18% on last 20 days</div>
          {/* Mini chart */}
          <svg viewBox="0 0 100 40" width="100%" height="36" preserveAspectRatio="none">
            <defs>
              <linearGradient id="chartGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#4F9B7A" stopOpacity="0.3" />
                <stop offset="100%" stopColor="#4F9B7A" stopOpacity="0" />
              </linearGradient>
            </defs>
            <path d="M0 35 L10 32 L20 28 L30 30 L40 22 L50 18 L60 20 L70 14 L80 10 L90 8 L100 5" stroke="#4F9B7A" strokeWidth="1.5" fill="none" strokeLinecap="round" />
            <path d="M0 35 L10 32 L20 28 L30 30 L40 22 L50 18 L60 20 L70 14 L80 10 L90 8 L100 5 L100 40 L0 40Z" fill="url(#chartGrad)" />
            {[0, 20, 40, 60, 80, 100].map((x, i) => (
              <text key={i} x={x === 100 ? 96 : x} y="40" fontSize="4" fill="#D1D5DB" textAnchor="middle">
                {['Apr 20', 'May 3', 'May 12', 'May 18', 'May 23', ''][i]}
              </text>
            ))}
          </svg>
        </div>

        {/* Top Growth Categories */}
        <div style={{ flex: 1, background: 'white', borderRadius: 8, padding: '7px 8px', border: '1px solid #E5EDE9', position: 'relative' }}>
          <div style={{ fontSize: 7, fontWeight: 700, color: '#143A33', marginBottom: 6 }}>Top Growth Categories</div>
          {[
            { name: 'Snacks', pct: '+24%', w: 90 },
            { name: 'Dairy', pct: '+21%', w: 80 },
            { name: 'Beverages', pct: '+17%', w: 65 },
            { name: 'Personal Care', pct: '+12%', w: 48 },
          ].map(({ name, pct, w }, i) => (
            <div key={name} style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              marginBottom: 4,
              animation: visible ? `slideIn 0.4s ease-out ${0.3 + i * 0.1}s both` : 'none',
            }}>
              <span style={{ fontSize: 6.5, color: '#374151', width: 55 }}>{name}</span>
              <span style={{ fontSize: 6.5, fontWeight: 700, color: '#16A34A' }}>{pct}</span>
            </div>
          ))}
          <div style={{ fontSize: 6, color: '#1F4D3F', fontWeight: 600, marginTop: 4, cursor: 'pointer' }}>View all</div>

          {/* Insight popup */}
          <div style={{
            position: 'absolute', top: 6, right: -2,
            background: 'white', borderRadius: 8, padding: '5px 7px',
            border: '1px solid #DDE7E3',
            boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
            width: 90,
            animation: visible ? 'fadeIn 0.5s ease-out 0.6s both' : 'none',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 3, marginBottom: 3 }}>
              <div style={{ width: 10, height: 10, borderRadius: '50%', background: 'rgba(139,92,246,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <svg viewBox="0 0 10 10" fill="none" width="6" height="6">
                  <path d="M5 1.5L6 4H8.5L6.5 5.5L7.5 8L5 6.5L2.5 8L3.5 5.5L1.5 4H4L5 1.5Z" fill="#8B5CF6" />
                </svg>
              </div>
              <span style={{ fontSize: 6, fontWeight: 700, color: '#7C3AED' }}>Insight</span>
            </div>
            <div style={{ fontSize: 6, color: '#374151', lineHeight: 1.4 }}>
              <span style={{ fontWeight: 700, color: '#143A33' }}>Snacks and Dairy</span>
              <br />driving strong growth<br />across all stores.
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

/* ─── Tablet Screen Content ─────────────────────────────── */
const TabletContent = ({ visible }: { visible: boolean }) => {
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const [activeIdx, setActiveIdx] = React.useState(0);

  const rows = [
    { city: 'Ahmedabad', rev: '₹48.2L', growth: '+14%', orders: '642', w: 92 },
    { city: 'Surat', rev: '₹36.1L', growth: '+18%', orders: '512', w: 87 },
    { city: 'Vadodara', rev: '₹22.8L', growth: '+12%', orders: '298', w: 78 },
    { city: 'Rajkot', rev: '₹18.3L', growth: '+9%', orders: '246', w: 74 },
  ];

  const handleScroll = () => {
    if (!scrollRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
    const maxScroll = scrollWidth - clientWidth;
    const idx = maxScroll > 0 ? Math.round((scrollLeft / maxScroll) * (rows.length - 1)) : 0;
    setActiveIdx(idx);
  };

  return (
    <div style={{ height: '100%', display: 'flex', overflow: 'hidden' }}>
      {/* Sidebar icons */}
      <div style={{
        width: 28, background: 'rgba(246,245,240,0.98)',
        borderRight: '1px solid #E5EDE9',
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        padding: '8px 0', gap: 8, flexShrink: 0,
      }}>
        <div style={{ width: 14, height: 14, borderRadius: '50%', background: '#1F4D3F', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <svg viewBox="0 0 10 10" fill="none" width="8" height="8">
            <rect x="2" y="2" width="6" height="6" rx="1" stroke="white" strokeWidth="1.2" />
          </svg>
        </div>
        {[0, 1, 2, 3, 4].map(i => (
          <div key={i} style={{ width: 14, height: 14, borderRadius: 4, background: i === 0 ? 'rgba(31,77,63,0.15)' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ width: 8, height: 8, borderRadius: 2, background: i === 0 ? '#1F4D3F' : '#D1D5DB', opacity: i === 0 ? 1 : 0.5 }} />
          </div>
        ))}
      </div>

      {/* Main */}
      <div style={{ flex: 1, padding: '8px 10px', display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <div style={{ fontSize: 9, fontWeight: 800, color: '#143A33' }}>Outlet Performance</div>
          <div style={{ padding: '2px 6px', borderRadius: 5, background: 'rgba(31,77,63,0.08)', border: '1px solid rgba(31,77,63,0.15)' }}>
            <span style={{ fontSize: 6, color: '#1F4D3F', fontWeight: 600 }}>This Month ▾</span>
          </div>
        </div>

        {/* Horizontal slider of outlet cards */}
        <div
          ref={scrollRef}
          onScroll={handleScroll}
          style={{
            display: 'flex',
            gap: 6,
            overflowX: 'auto',
            overflowY: 'hidden',
            scrollSnapType: 'x mandatory',
            WebkitOverflowScrolling: 'touch',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            flex: 1,
            paddingBottom: 2,
          }}
        >
          {rows.map(({ city, rev, growth, orders, w }, i) => (
            <div
              key={city}
              style={{
                minWidth: 120,
                maxWidth: 130,
                flexShrink: 0,
                scrollSnapAlign: 'start',
                background: 'white',
                borderRadius: 8,
                padding: '8px 8px',
                border: '1px solid #E5EDE9',
                boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
                display: 'flex',
                flexDirection: 'column',
                gap: 4,
                animation: visible ? `fadeUp 0.4s ease-out ${0.15 + i * 0.1}s both` : 'none',
              }}
            >
              {/* City name */}
              <div style={{ fontSize: 7.5, fontWeight: 800, color: '#143A33' }}>{city}</div>
              <div style={{ fontSize: 5.5, color: '#9CA3AF' }}>Outlet</div>

              {/* Revenue */}
              <div style={{ marginTop: 2 }}>
                <div style={{ fontSize: 5.5, color: '#9CA3AF', marginBottom: 1 }}>Revenue</div>
                <div style={{ fontSize: 11, fontWeight: 800, color: '#143A33' }}>{rev}</div>
              </div>

              {/* Growth */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{
                  fontSize: 7, fontWeight: 700, color: '#16A34A',
                  background: 'rgba(22,163,74,0.1)', borderRadius: 4,
                  padding: '1px 4px',
                }}>{growth}</span>
                <span style={{ fontSize: 5.5, color: '#9CA3AF' }}>growth</span>
              </div>

              {/* Orders */}
              <div>
                <div style={{ fontSize: 5.5, color: '#9CA3AF', marginBottom: 1 }}>Orders</div>
                <div style={{ fontSize: 9, fontWeight: 700, color: '#374151' }}>{orders}</div>
              </div>

              {/* Trend bar */}
              <div style={{ height: 3, background: '#E6F1FB', borderRadius: 2, overflow: 'hidden', marginTop: 2 }}>
                <div style={{
                  height: '100%', width: `${w}%`, background: '#4F9B7A', borderRadius: 2,
                  animation: visible ? `barGrow 0.8s ease-out ${0.3 + i * 0.1}s both` : 'none',
                }} />
              </div>
            </div>
          ))}
        </div>

        {/* Scroll indicator dots */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: 4, flexShrink: 0, paddingTop: 2 }}>
          {rows.map((_, i) => (
            <div
              key={i}
              style={{
                width: i === activeIdx ? 12 : 4,
                height: 4,
                borderRadius: 2,
                background: i === activeIdx ? '#1F4D3F' : '#DDE7E3',
                transition: 'width 0.25s ease, background 0.25s ease',
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

/* ─── Mobile Screen Content ─────────────────────────────── */
const MobileContent = ({ visible }: { visible: boolean }) => (
  <div style={{ padding: '6px 8px', height: '100%', display: 'flex', flexDirection: 'column', gap: 5 }}>
    {/* Header */}
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <div style={{ width: 12, height: 12, borderRadius: '50%', background: '#1F4D3F', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <svg viewBox="0 0 10 10" fill="none" width="7" height="7">
            <circle cx="5" cy="5" r="2" fill="white" />
          </svg>
        </div>
        <span style={{ fontSize: 7, fontWeight: 800, color: '#1F4D3F' }}>VINA</span>
      </div>
      <div style={{ width: 16, height: 16, borderRadius: '50%', background: '#1F4D3F', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ fontSize: 6, color: 'white', fontWeight: 700 }}>S</span>
      </div>
    </div>

    <div style={{ fontSize: 8.5, fontWeight: 800, color: '#143A33' }}>Morning Briefing</div>
    <div style={{ fontSize: 6, color: '#9CA3AF' }}>Today, 9:41 AM</div>

    {/* Status items */}
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1 }}>
      {[
        { icon: '✓', label: 'Revenue On Track', sub: '₹16.4L · +9% vs yesterday', bg: '#F0FDF4', border: '#BBF7D0', iconBg: '#16A34A', textColor: '#166534' },
        { icon: '⚠', label: '3 Inventory Alerts', sub: 'High priority items', bg: '#FFFBEB', border: '#FDE68A', iconBg: '#D97706', textColor: '#92400E' },
        { icon: '!', label: '6 Overdue Customers', sub: 'Action recommended', bg: '#FEF2F2', border: '#FECACA', iconBg: '#DC2626', textColor: '#991B1B' },
        { icon: '↗', label: 'PO Draft Ready', sub: '14 items · ₹4.2L', bg: '#EFF6FF', border: '#BFDBFE', iconBg: '#2563EB', textColor: '#1D4ED8' },
      ].map(({ icon, label, sub, bg, border, iconBg, textColor }, i) => (
        <div key={label} style={{
          display: 'flex', alignItems: 'center', gap: 6,
          padding: '5px 6px', borderRadius: 7,
          background: bg, border: `1px solid ${border}`,
          animation: visible ? `slideIn 0.4s ease-out ${0.2 + i * 0.12}s both` : 'none',
        }}>
          <div style={{ width: 16, height: 16, borderRadius: '50%', background: iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <span style={{ fontSize: 8, color: 'white', fontWeight: 700 }}>{icon}</span>
          </div>
          <div>
            <div style={{ fontSize: 7, fontWeight: 700, color: textColor }}>{label}</div>
            <div style={{ fontSize: 5.5, color: '#9CA3AF' }}>{sub}</div>
          </div>
        </div>
      ))}
    </div>

    {/* Input bar */}
    <div style={{
      display: 'flex', alignItems: 'center', gap: 4,
      padding: '4px 7px', borderRadius: 8,
      background: 'rgba(255,255,255,0.9)', border: '1px solid #DDE7E3',
    }}>
      <span style={{ fontSize: 6, color: '#9CA3AF', flex: 1 }}>Ask VINA anything...</span>
      <div style={{ width: 14, height: 14, borderRadius: '50%', background: '#1F4D3F', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <svg viewBox="0 0 10 10" fill="none" width="7" height="7">
          <path d="M2 5h6M6 3l2 2-2 2" stroke="white" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
    </div>
  </div>
);

/* ─── Signal Cards Slider ────────────────────────────────── */
const SIGNAL_CARDS = [
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" width="20" height="20">
        <path d="M3 3v18h18" stroke="#1F4D3F" strokeWidth="2" strokeLinecap="round" />
        <path d="M7 16l4-4 4 4 4-6" stroke="#1F4D3F" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
    iconBg: 'rgba(31,77,63,0.1)',
    label: 'Revenue Opportunity',
    value: '₹4.2L',
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" width="20" height="20">
        <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z" stroke="#5B6FE8" strokeWidth="2" />
      </svg>
    ),
    iconBg: 'rgba(91,111,232,0.1)',
    label: '7 Growth Products',
    value: 'Identified',
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" width="20" height="20">
        <path d="M7 17L17 7M17 7H7M17 7v10" stroke="#E8855B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
    iconBg: 'rgba(232,133,91,0.1)',
    label: 'Expansion Potential',
    value: 'High',
  },
];

const SignalCardsSlider = ({ visible }: { visible: boolean }) => {
  const sliderRef = React.useRef<HTMLDivElement>(null);
  const [activeIdx, setActiveIdx] = React.useState(0);

  const handleScroll = () => {
    if (!sliderRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = sliderRef.current;
    const maxScroll = scrollWidth - clientWidth;
    const idx = maxScroll > 0 ? Math.round((scrollLeft / maxScroll) * (SIGNAL_CARDS.length - 1)) : 0;
    setActiveIdx(idx);
  };

  const scrollTo = (idx: number) => {
    if (!sliderRef.current) return;
    const { scrollWidth, clientWidth } = sliderRef.current;
    const maxScroll = scrollWidth - clientWidth;
    sliderRef.current.scrollTo({ left: (maxScroll / (SIGNAL_CARDS.length - 1)) * idx, behavior: 'smooth' });
  };

  return (
    <div
      style={{
        marginTop: 24,
        animation: visible ? 'fadeUp 0.7s ease-out 0.5s both' : 'none',
      }}
    >
      {/* Slider track */}
      <div
        ref={sliderRef}
        onScroll={handleScroll}
        className="signal-slider"
        style={{
          display: 'flex',
          gap: 12,
          overflowX: 'auto',
          overflowY: 'hidden',
          scrollSnapType: 'x mandatory',
          WebkitOverflowScrolling: 'touch',
          paddingBottom: 4,
          paddingLeft: 2,
          paddingRight: 2,
        }}
      >
        {SIGNAL_CARDS.map(({ icon, iconBg, label, value }, i) => (
          <div
            key={label}
            style={{
              flex: '0 0 calc(33.333% - 8px)',
              minWidth: 140,
              scrollSnapAlign: 'start',
              background: 'rgba(255,255,255,0.82)',
              border: '1px solid #DDE7E3',
              borderRadius: 14,
              padding: '14px 12px',
              backdropFilter: 'blur(12px)',
              boxShadow: '0 4px 20px rgba(0,0,0,0.06), 0 1px 4px rgba(0,0,0,0.04)',
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              transition: 'transform 0.2s ease, box-shadow 0.2s ease',
              cursor: 'default',
              animation: visible ? `fadeUp 0.5s ease-out ${0.6 + i * 0.1}s both` : 'none',
            }}
            onMouseEnter={e => {
              (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-2px)';
              (e.currentTarget as HTMLDivElement).style.boxShadow = '0 8px 28px rgba(0,0,0,0.1)';
            }}
            onMouseLeave={e => {
              (e.currentTarget as HTMLDivElement).style.transform = 'translateY(0)';
              (e.currentTarget as HTMLDivElement).style.boxShadow = '0 4px 20px rgba(0,0,0,0.06), 0 1px 4px rgba(0,0,0,0.04)';
            }}
          >
            <div style={{
              width: 36, height: 36, borderRadius: 10,
              background: iconBg,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}>
              {icon}
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 11, color: '#5D6B66', marginBottom: 3, lineHeight: 1.3, wordBreak: 'break-word' }}>{label}</div>
              <div style={{ fontSize: 16, fontWeight: 800, color: '#143A33' }}>{value}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Scroll indicator dots */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: 6, marginTop: 12 }}>
        {SIGNAL_CARDS.map((_, i) => (
          <button
            key={i}
            onClick={() => scrollTo(i)}
            style={{
              width: i === activeIdx ? 20 : 6,
              height: 6,
              borderRadius: 3,
              background: i === activeIdx ? '#1F4D3F' : '#DDE7E3',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              transition: 'width 0.25s ease, background 0.25s ease',
            }}
          />
        ))}
      </div>
    </div>
  );
};

/* ─── Main Component ─────────────────────────────────────── */
export default function IntelligenceAcrossDevices() {
  const [visible, setVisible] = useState(false);
  const [kpiVisible, setKpiVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          setTimeout(() => setKpiVisible(true), 300);
        }
      },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative overflow-hidden"
      style={{
        background: 'linear-gradient(180deg, #E6F1FB 0%, #EEF3F7 30%, #F6F5F0 65%, #FBFAF6 100%)',
        padding: '80px 0',
      }}
    >
      <style>{STYLES}</style>

      {/* Ambient glows */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div style={{
          position: 'absolute', top: '5%', left: '10%',
          width: 600, height: 600, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(184,212,232,0.28) 0%, transparent 70%)',
          animation: 'glowPulse 8s ease-in-out infinite',
        }} />
        <div style={{
          position: 'absolute', bottom: '15%', left: '25%',
          width: 400, height: 400, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(200,230,212,0.22) 0%, transparent 70%)',
          animation: 'glowPulse 10s ease-in-out infinite 2s',
        }} />
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 relative z-10">
        <div className="flex flex-col lg:flex-row items-start gap-12 lg:gap-8">

          {/* ══════════════════════════════════════
              LEFT SIDE — 55% — Device Ecosystem
          ══════════════════════════════════════ */}
          <div
            className="w-full lg:w-[55%]"
            style={{
              animation: visible ? 'fadeUp 0.8s ease-out 0.1s both' : 'none',
            }}
          >
            {/* Device stack container — fixed height, overflow hidden */}
            <div
              style={{
                position: 'relative',
                width: '100%',
                height: 520,
                overflow: 'hidden',
              }}
            >
              {/* ── Laptop (top, centered) ── */}
              <div
                className="laptop-float"
                style={{
                  position: 'absolute',
                  top: 0,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  zIndex: 10,
                  width: '90%',
                  maxWidth: 520,
                }}
              >
                {/* Laptop lid */}
                <div style={{
                  background: 'linear-gradient(180deg, #2c2c2e 0%, #1c1c1e 100%)',
                  borderRadius: '16px 16px 0 0',
                  padding: '10px 10px 0',
                  boxShadow: '0 32px 80px rgba(0,0,0,0.2), 0 8px 24px rgba(31,77,63,0.08), inset 0 1px 0 rgba(255,255,255,0.08)',
                }}>
                  {/* Camera notch */}
                  <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 5 }}>
                    <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#3a3a3c' }} />
                  </div>
                  {/* Screen bezel */}
                  <div style={{ background: '#0c0c0e', borderRadius: '8px 8px 0 0', padding: 4 }}>
                    {/* Browser chrome */}
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 6,
                      padding: '5px 10px',
                      background: 'rgba(248,250,249,0.98)',
                      borderBottom: '1px solid #E5EDE9',
                    }}>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#FF5F57' }} />
                        <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#FFBD2E' }} />
                        <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#28CA41' }} />
                      </div>
                      <div style={{
                        flex: 1, height: 16, borderRadius: 8,
                        background: 'rgba(255,255,255,0.9)',
                        border: '1px solid #DDE7E3',
                        display: 'flex', alignItems: 'center', padding: '0 8px',
                      }}>
                        <span style={{ fontSize: 7, color: '#9CA3AF' }}>app.vina.ai/workspace</span>
                      </div>
                    </div>
                    {/* Screen content */}
                    <div style={{
                      background: 'linear-gradient(135deg, #F8FAF9 0%, #F2F6F4 100%)',
                      height: 240,
                      overflow: 'hidden',
                    }}>
                      <LaptopContent visible={kpiVisible} />
                    </div>
                  </div>
                </div>
                {/* Hinge */}
                <div style={{ height: 4, background: 'linear-gradient(180deg, #1c1c1e 0%, #2c2c2e 100%)' }} />
                {/* Base */}
                <div style={{
                  background: 'linear-gradient(180deg, #d8d8da 0%, #c0c0c2 100%)',
                  borderRadius: '0 0 12px 12px',
                  height: 18,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                }}>
                  <div style={{ width: 60, height: 8, borderRadius: 4, background: 'rgba(0,0,0,0.08)' }} />
                </div>
              </div>

              {/* ── Tablet (bottom-left) ── */}
              <div
                className="tablet-float"
                style={{
                  position: 'absolute',
                  left: '2%',
                  bottom: 0,
                  zIndex: 20,
                }}
              >
                <div style={{ width: 200 }}>
                  <div style={{
                    background: 'linear-gradient(180deg, #2a2a2c 0%, #1e1e20 100%)',
                    borderRadius: 18,
                    padding: '10px 8px',
                    boxShadow: '0 24px 64px rgba(0,0,0,0.22), 0 6px 20px rgba(31,77,63,0.08), inset 0 1px 0 rgba(255,255,255,0.06)',
                  }}>
                    {/* Camera */}
                    <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 6 }}>
                      <div style={{ width: 5, height: 5, borderRadius: '50%', background: '#3a3a3c' }} />
                    </div>
                    {/* Screen */}
                    <div style={{
                      background: 'linear-gradient(135deg, #F8FAF9 0%, #F2F6F4 100%)',
                      borderRadius: 10,
                      overflow: 'hidden',
                      height: 240,
                    }}>
                      {/* Status bar */}
                      <div style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '4px 8px',
                        background: 'rgba(248,250,249,0.98)',
                        borderBottom: '1px solid #E5EDE9',
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <div style={{ width: 12, height: 12, borderRadius: '50%', background: '#1F4D3F', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <svg viewBox="0 0 10 10" fill="none" width="7" height="7">
                              <rect x="2" y="2" width="6" height="6" rx="1" stroke="white" strokeWidth="1.2" />
                            </svg>
                          </div>
                          <span style={{ fontSize: 7, fontWeight: 700, color: '#1F4D3F' }}>VINA</span>
                        </div>
                        <div style={{ width: 20, height: 6, borderRadius: 3, background: '#DDE7E3' }} />
                      </div>
                      <TabletContent visible={kpiVisible} />
                    </div>
                    {/* Home bar */}
                    <div style={{ display: 'flex', justifyContent: 'center', marginTop: 6 }}>
                      <div style={{ width: 30, height: 3, borderRadius: 2, background: 'rgba(255,255,255,0.15)' }} />
                    </div>
                  </div>
                </div>
              </div>

              {/* ── Mobile (bottom-right) ── */}
              <div
                className="mobile-float"
                style={{
                  position: 'absolute',
                  right: '4%',
                  bottom: 0,
                  zIndex: 25,
                }}
              >
                <div style={{ width: 130 }}>
                  <div style={{
                    background: 'linear-gradient(180deg, #2a2a2c 0%, #1e1e20 100%)',
                    borderRadius: 30,
                    padding: '12px 6px',
                    boxShadow: '0 22px 60px rgba(0,0,0,0.22), 0 5px 18px rgba(31,77,63,0.08), inset 0 1px 0 rgba(255,255,255,0.06)',
                  }}>
                    {/* Dynamic island */}
                    <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 6 }}>
                      <div style={{
                        width: 42, height: 10, borderRadius: 6,
                        background: '#0a0a0c',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3,
                      }}>
                        <div style={{ width: 3, height: 3, borderRadius: '50%', background: '#1a1a1c' }} />
                        <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#1a1a1c' }} />
                      </div>
                    </div>
                    {/* Screen */}
                    <div style={{
                      background: 'linear-gradient(135deg, #F8FAF9 0%, #F2F6F4 100%)',
                      borderRadius: 20,
                      overflow: 'hidden',
                      height: 260,
                    }}>
                      {/* Status bar */}
                      <div style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '4px 8px',
                        background: 'rgba(248,250,249,0.98)',
                        borderBottom: '1px solid #E5EDE9',
                      }}>
                        <span style={{ fontSize: 6, color: '#374151', fontWeight: 700 }}>9:41</span>
                        <div style={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                          <svg viewBox="0 0 16 10" fill="none" width="12" height="8">
                            <rect x="0" y="3" width="3" height="7" rx="0.5" fill="#374151" />
                            <rect x="4.5" y="2" width="3" height="8" rx="0.5" fill="#374151" />
                            <rect x="9" y="0.5" width="3" height="9.5" rx="0.5" fill="#374151" />
                            <rect x="13.5" y="2" width="2" height="8" rx="0.5" fill="#D1D5DB" />
                          </svg>
                          <div style={{ width: 14, height: 7, borderRadius: 2, border: '1px solid #9CA3AF', padding: '1px', display: 'flex', alignItems: 'center' }}>
                            <div style={{ width: '75%', height: '100%', background: '#16A34A', borderRadius: 1 }} />
                          </div>
                        </div>
                      </div>
                      <MobileContent visible={kpiVisible} />
                    </div>
                    {/* Home bar */}
                    <div style={{ display: 'flex', justifyContent: 'center', marginTop: 6 }}>
                      <div style={{ width: 36, height: 3, borderRadius: 2, background: 'rgba(255,255,255,0.18)' }} />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* ── 3 Signal Cards Slider ── */}
            <SignalCardsSlider visible={visible} />
          </div>

          {/* ══════════════════════════════════════
              RIGHT SIDE — 45% — Content
          ══════════════════════════════════════ */}
          <div
            className="w-full lg:w-[45%] flex flex-col justify-center"
            style={{
              paddingTop: 20,
              animation: visible ? 'fadeUp 0.8s ease-out 0.25s both' : 'none',
            }}
          >
            {/* Pill label */}
            <div style={{ marginBottom: 20 }}>
              <span style={{
                display: 'inline-block',
                padding: '6px 16px',
                borderRadius: 999,
                border: '1.5px solid #DDE7E3',
                background: 'rgba(255,255,255,0.7)',
                backdropFilter: 'blur(8px)',
                fontSize: 11,
                fontWeight: 700,
                color: '#143A33',
                letterSpacing: '0.12em',
              }}>
                INTELLIGENCE EVERYWHERE
              </span>
            </div>

            {/* Headline */}
            <h2 style={{
              fontSize: 'clamp(28px, 3.2vw, 44px)',
              fontWeight: 800,
              color: '#143A33',
              lineHeight: 1.15,
              marginBottom: 16,
              letterSpacing: '-0.02em',
            }}>
              One intelligence layer.<br />
              Everywhere business happens.
            </h2>

            {/* Subheadline */}
            <p style={{
              fontSize: 16,
              color: '#5D6B66',
              lineHeight: 1.65,
              marginBottom: 32,
              maxWidth: 420,
            }}>
              Analyze performance, monitor operations, and take action from anywhere. VINA keeps your business context connected across every screen.
            </p>

            {/* Feature cards */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 36 }}>
              {[
                {
                  iconBg: 'rgba(31,77,63,0.1)',
                  icon: (
                    <svg viewBox="0 0 24 24" fill="none" width="22" height="22">
                      <path d="M3 3v18h18" stroke="#1F4D3F" strokeWidth="2" strokeLinecap="round" />
                      <path d="M7 16l4-4 4 4 4-6" stroke="#1F4D3F" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  ),
                  title: 'Analyze',
                  desc: 'Explore inventory, sales, finance, and operational trends with complete visibility.',
                },
                {
                  iconBg: 'rgba(91,111,232,0.1)',
                  icon: (
                    <svg viewBox="0 0 24 24" fill="none" width="22" height="22">
                      <path d="M12 2v10l4 4" stroke="#5B6FE8" strokeWidth="2" strokeLinecap="round" />
                      <circle cx="12" cy="12" r="9" stroke="#5B6FE8" strokeWidth="2" />
                      <path d="M12 2a9 9 0 019 9" stroke="#5B6FE8" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                  ),
                  title: 'Monitor',
                  desc: 'Track performance across outlets, teams, and categories in real time.',
                },
                {
                  iconBg: 'rgba(232,133,91,0.1)',
                  icon: (
                    <svg viewBox="0 0 24 24" fill="none" width="22" height="22">
                      <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" stroke="#E8855B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  ),
                  title: 'Act',
                  desc: 'Turn recommendations into business actions wherever decisions happen.',
                },
              ].map(({ iconBg, icon, title, desc }, i) => (
                <div
                  key={title}
                  style={{
                    display: 'flex', alignItems: 'flex-start', gap: 16,
                    padding: '18px 20px',
                    background: 'rgba(255,255,255,0.82)',
                    border: '1px solid #DDE7E3',
                    borderRadius: 16,
                    backdropFilter: 'blur(12px)',
                    boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
                    transition: 'transform 0.25s ease, box-shadow 0.25s ease',
                    cursor: 'default',
                    animation: visible ? `fadeUp 0.5s ease-out ${0.4 + i * 0.12}s both` : 'none',
                  }}
                  onMouseEnter={e => {
                    (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-2px)';
                    (e.currentTarget as HTMLDivElement).style.boxShadow = '0 8px 28px rgba(31,77,63,0.08)';
                  }}
                  onMouseLeave={e => {
                    (e.currentTarget as HTMLDivElement).style.transform = 'translateY(0)';
                    (e.currentTarget as HTMLDivElement).style.boxShadow = '0 2px 12px rgba(0,0,0,0.04)';
                  }}
                >
                  <div style={{
                    width: 44, height: 44, borderRadius: 12,
                    background: iconBg,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    {icon}
                  </div>
                  <div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: '#143A33', marginBottom: 4 }}>{title}</div>
                    <div style={{ fontSize: 14, color: '#5D6B66', lineHeight: 1.55 }}>{desc}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Bottom trust message */}
          </div>

        </div>
      </div>
    </section>
  );
}
